import React from 'react';
import { Alert, Button, Form, Modal, Offcanvas } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { fetchApi, makeResetCache, notifyError, notifyResponse } from 'gossamer-front-end';
import { UserAddCoHostRequest } from 'cb-model';
import { useIsMobile } from './hooks/useIsMobile';

interface AddCoHostModalProps {
  eventId: number;
  show: boolean;
  setShow: (val: boolean) => void;
}

const validateEmail = /^.+@.+\..+$/;

export const AddCoHostModal = (props: AddCoHostModalProps) => {
  const [email, setEmail] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [processing, setProcessing] = React.useState(false);
  const dispatch = useDispatch();
  const resetCache = makeResetCache(dispatch);
  const isMobile = useIsMobile(992); // bootstrap lg breakpoint

  React.useEffect(() => {
    if (!email) {
      setError('Email address required');
    } else if (!validateEmail.exec(email)) {
      setError('Please enter a valid email address');
    } else {
      setError(null);
    }
  }, [email]);

  const handleSave = async () => {
    if (error) {
      return;
    }
    setProcessing(true);
    try {
      const response = await fetchApi('POST', `/main/add-co-host`, {
        eventId: props.eventId,
        email,
      } as UserAddCoHostRequest);
      notifyResponse(response);
      resetCache('EventCoHost');
      handleClose();
    } catch (e) {
      notifyError(e, 'An error occurred while adding the co-host');
    }
    setProcessing(false);
  };

  const handleClose = () => {
    setTouched(false);
    setError(null);
    props.setShow(false);
    setEmail('');
  };

  const title = 'Add a Co-Host';

  const formContent = (
    <div className={isMobile ? 'px-3 pt-3 pb-3' : 'p-3'}>
      <Form.Label>Add additional hosts by email address</Form.Label>
      <Form.Control
        type="email"
        placeholder=""
        value={email}
        onChange={(event) => {
          setTouched(true);
          setEmail(event.target.value);
        }}
        onBlur={() => setTouched(true)}
      />
      <div className="d-flex gap-2 justify-content-end mt-3">
        <Button variant="outline-secondary" onClick={handleClose} disabled={processing}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={processing || !!error || !email.trim()}>
          Invite
        </Button>
      </div>
      {touched && !!error && (
        <Alert className="mt-4" variant="danger">
          {error}
        </Alert>
      )}
    </div>
  );

  if (!isMobile) {
    return (
      <Modal show={props.show} onHide={handleClose} centered backdrop="static">
        <Modal.Header closeButton={!processing}>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        {formContent}
      </Modal>
    );
  }

  return (
    <Offcanvas
      show={props.show}
      onHide={handleClose}
      placement="bottom"
      backdrop="static"
      scroll={false}
      style={{ height: 'auto' }}
    >
      <Offcanvas.Header closeButton={!processing}>
        <Offcanvas.Title>{title}</Offcanvas.Title>
      </Offcanvas.Header>

      <div className="d-flex flex-column">
        <div className="flex-grow-1 overflow-auto">{formContent}</div>
      </div>
    </Offcanvas>
  );
};
